import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CameraIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import MemoryPanel from '../../components/MemoryPanel';
import SoulSeedPanel from '../../components/SoulSeedPanel';
import RitualPhrasePanel from '../../components/RitualPhrasePanel';
import InspirationBoardPanel from '../../components/InspirationBoardPanel';

interface CodexEntry {
  id: string;
  originalFilename: string;
  label?: string;
  category?: string;
  compressedContent: string;
  fullText?: string;
  isFullText?: boolean;
  facts: string[];
  storylines: string[];
  originalCharCount: number;
  compressedCharCount: number;
  currentVersion: number;
  createdAt: string;
}

interface Companion {
  id: string;
  name: string;
  gender: string;
  personality: string;
  model: string;
  avatarType: string;
  avatarUrl?: string;
  sexualPreference?: string;
  systemPrompt?: string;
  responseLength?: string;
  openingRitual?: string;
  closingRitual?: string;
  narrativePulseEnabled?: boolean;
  narrativePulseStyle?: string;
  narrativePulseFrequency?: string;
}

const CompanionSettings: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [companion, setCompanion] = useState<Companion | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [selectedEmojiAvatar, setSelectedEmojiAvatar] = useState<string>('');
  const [uploadedImage, setUploadedImage] = useState<string>('');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [codexes, setCodexes] = useState<CodexEntry[]>([]);
  const [isUploadingCodex, setIsUploadingCodex] = useState(false);
  const [expandedCodexId, setExpandedCodexId] = useState<string | null>(null);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const [showSoulSeedPanel, setShowSoulSeedPanel] = useState(false);
  const [showRitualPhrasePanel, setShowRitualPhrasePanel] = useState(false);
  const [showInspirationBoard, setShowInspirationBoard] = useState(false);
  const [codexSearch, setCodexSearch] = useState('');
  const [selectedCodexIds, setSelectedCodexIds] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [pendingCodexFile, setPendingCodexFile] = useState<File | null>(null);
  const [codexLabel, setCodexLabel] = useState('');
  const [codexCategory, setCodexCategory] = useState<'instructional' | 'timeline'>('instructional');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const codexFileInputRef = useRef<HTMLInputElement>(null);

  const defaultAvatars = [
    { id: '1', emoji: '🌟', bg: 'bg-purple-500' },
    { id: '2', emoji: '🌙', bg: 'bg-blue-500' },
    { id: '3', emoji: '🌸', bg: 'bg-pink-500' },
    { id: '4', emoji: '🍀', bg: 'bg-green-500' },
    { id: '5', emoji: '🔮', bg: 'bg-indigo-500' },
    { id: '6', emoji: '🦋', bg: 'bg-teal-500' },
    { id: '7', emoji: '🌈', bg: 'bg-yellow-500' },
    { id: '8', emoji: '⚡', bg: 'bg-orange-500' },
  ];

  const [userPlan, setUserPlan] = useState<string>('free');

  useEffect(() => {
    axios
      .get('/users/usage')
      .then((res) => setUserPlan(res.data?.plan || 'free'))
      .catch(() => setUserPlan('free'));
  }, []);

  const isFreeTier = userPlan === 'free';

  const modelOptions = [
    // OpenAI
    {
      value: 'gpt-4.1-mini-2025-04-14',
      label: 'GPT-4.1 Mini',
      cost: '0.5 credits/msg',
      locked: false,
    },
    {
      value: 'gpt-4.1-2025-04-14',
      label: 'GPT-4.1',
      cost: '1 credit/msg',
      locked: isFreeTier,
    },
    {
      value: 'gpt-4o',
      label: 'GPT-4o',
      cost: '1.5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'gpt-5.2',
      label: 'GPT-5.2',
      cost: '2 credits/msg',
      locked: isFreeTier,
    },
    // Anthropic Claude
    {
      value: 'claude-opus-4-5-20250527',
      label: 'Claude Opus 4.5',
      cost: '5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'claude-sonnet-4-5-20250514',
      label: 'Claude Sonnet 4.5',
      cost: '1.5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'claude-opus-4-20250514',
      label: 'Claude Opus 4',
      cost: '5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'claude-sonnet-4-20250514',
      label: 'Claude Sonnet 4',
      cost: '1.5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'claude-3.5-sonnet-20241022',
      label: 'Claude 3.5 Sonnet',
      cost: '1.5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'claude-3.5-haiku-20241022',
      label: 'Claude 3.5 Haiku',
      cost: '0.5 credits/msg',
      locked: isFreeTier,
    },
    // Google Gemini
    {
      value: 'gemini-2.5-flash',
      label: 'Gemini 2.5 Flash',
      cost: '0.5 credits/msg',
      locked: isFreeTier,
    },
    {
      value: 'gemini-2.5-pro',
      label: 'Gemini 2.5 Pro',
      cost: '1 credit/msg',
      locked: isFreeTier,
    },
  ];

  const avatarTypeOptions = [
    { value: 'icon', label: t('companionSettings.avatarTypes.icon', 'Icon') },
    { value: 'photo', label: t('companionSettings.avatarTypes.photo', 'Photo') },
    { value: 'generated', label: t('companionSettings.avatarTypes.generated', 'AI Generated') },
  ];

  const genderOptions = [
    { value: 'male', label: t('companions.male', 'Male') },
    { value: 'female', label: t('companions.female', 'Female') },
    { value: 'non-binary', label: t('companions.nonBinary', 'Non-binary') },
  ];

  const sexualPreferenceOptions = [
    { value: 'straight', label: t('preferences.sexualPreferences.straight', 'Straight') },
    { value: 'gay', label: t('preferences.sexualPreferences.gay', 'Gay') },
    { value: 'bisexual', label: t('preferences.sexualPreferences.bisexual', 'Bisexual') },
    { value: 'pansexual', label: t('preferences.sexualPreferences.pansexual', 'Pansexual') },
    { value: 'asexual', label: t('preferences.sexualPreferences.asexual', 'Asexual') },
  ];

  const responseLengthOptions = [
    {
      value: 'concise',
      label: t('companionSettings.responseLength.concise', 'Concise'),
      description: t(
        'companionSettings.responseLength.conciseDesc',
        'Short, snappy replies — good for quick chats',
      ),
    },
    {
      value: 'balanced',
      label: t('companionSettings.responseLength.balanced', 'Balanced (Default)'),
      description: t(
        'companionSettings.responseLength.balancedDesc',
        'Natural conversation length — works for most situations',
      ),
    },
    {
      value: 'expressive',
      label: t('companionSettings.responseLength.expressive', 'Expressive'),
      description: t(
        'companionSettings.responseLength.expressiveDesc',
        'Longer, detailed responses — great for stories and deep conversations',
      ),
    },
  ];

  useEffect(() => {
    fetchCompanion();
    fetchCodexes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCompanion = async () => {
    try {
      const response = await axios.get(`/companions/${id}`);
      setCompanion(response.data);
    } catch (err) {
      console.error('Failed to load companion:', err);
      toast.error(t('companionSettings.errors.loadFailed', 'Failed to load companion settings'));
      navigate('/profile');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchCodexes = async () => {
    try {
      const response = await axios.get(`/companions/${id}/codex`);
      setCodexes(response.data);
    } catch (err) {
      console.error('Failed to load codexes:', err);
    }
  };

  const handleCodexFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !companion) return;

    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
    ];
    if (!validTypes.includes(file.type)) {
      toast.error(
        t('codex.invalidFileType', 'Please upload a PDF, Word document (.docx), or text file.'),
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('codex.fileTooLarge', 'File size must be less than 10MB'));
      return;
    }

    // Set file and default label from filename
    setPendingCodexFile(file);
    setCodexLabel(file.name.replace(/\.[^/.]+$/, ''));
    setCodexCategory('instructional');

    // Reset file input
    if (codexFileInputRef.current) codexFileInputRef.current.value = '';
  };

  const handleCodexUpload = async () => {
    if (!pendingCodexFile || !companion) return;

    setIsUploadingCodex(true);
    try {
      const formData = new FormData();
      formData.append('file', pendingCodexFile);
      formData.append('label', codexLabel);
      formData.append('category', codexCategory);

      await axios.post(`/companions/${id}/codex/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('codex.uploadSuccess', 'Personality codex processed successfully!'));
      fetchCodexes();
      setPendingCodexFile(null);
      setCodexLabel('');
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t('codex.uploadFailed', 'Failed to process document. Please try again.'),
      );
    } finally {
      setIsUploadingCodex(false);
    }
  };

  const handleUpdateCodex = async (
    codexId: string,
    updates: { label?: string; category?: string },
  ) => {
    try {
      await axios.patch(`/companions/${id}/codex/${codexId}`, updates);
      fetchCodexes();
    } catch (err: any) {
      toast.error(t('codex.updateFailed', 'Failed to update codex.'));
    }
  };

  const handleDeleteCodex = async (codexId: string) => {
    try {
      await axios.delete(`/companions/${id}/codex/${codexId}`);
      toast.success(t('codex.deleteSuccess', 'Codex removed'));
      setCodexes((prev) => prev.filter((c) => c.id !== codexId));
    } catch (err) {
      toast.error(t('codex.deleteFailed', 'Failed to delete codex'));
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !companion) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(
        t(
          'companionSettings.avatar.invalidFileType',
          'Please select a valid image file (JPEG, PNG, GIF, or WebP)',
        ),
      );
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t('companionSettings.avatar.fileTooLarge', 'File size must be less than 5MB'));
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post(`/companions/${id}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update companion with new avatar URL
      setCompanion({ ...companion, avatarUrl: response.data.avatarUrl });
      setUploadedImage(response.data.avatarUrl);
      setSelectedEmojiAvatar('');
      toast.success(t('companionSettings.avatar.uploadSuccess', 'Avatar uploaded successfully'));
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t('companionSettings.avatar.uploadFailed', 'Failed to upload avatar'),
      );
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!companion) return;

    try {
      await axios.delete(`/companions/${id}/avatar`);
      setCompanion({ ...companion, avatarUrl: undefined });
      setUploadedImage('');
      toast.success(t('companionSettings.avatar.deleteSuccess', 'Avatar deleted successfully'));
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t('companionSettings.avatar.deleteFailed', 'Failed to delete avatar'),
      );
    }
  };

  const handleEmojiAvatarSelect = (avatarId: string) => {
    setSelectedEmojiAvatar(avatarId);
    if (companion) {
      setCompanion({ ...companion, avatarUrl: avatarId });
    }
  };

  const handleSaveSettings = async () => {
    if (!companion) return;

    setIsLoading(true);
    try {
      await axios.put(`/companions/${id}/settings`, {
        name: companion.name,
        gender: companion.gender,
        personality: companion.personality,
        model: companion.model,
        avatarType: companion.avatarType,
        sexualPreference: companion.sexualPreference,
        systemPrompt: companion.systemPrompt,
        responseLength: companion.responseLength,
        openingRitual: companion.openingRitual,
        closingRitual: companion.closingRitual,
        narrativePulseEnabled: companion.narrativePulseEnabled,
        narrativePulseStyle: companion.narrativePulseStyle,
        narrativePulseFrequency: companion.narrativePulseFrequency,
      });

      toast.success(t('companionSettings.saveSuccess', 'Settings saved successfully'));
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t('companionSettings.errors.saveFailed', 'Failed to save settings'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-light-text-secondary dark:text-dark-text-secondary">
            {t('common.loading', 'Loading...')}
          </div>
        </div>
      </MobileLayout>
    );
  }

  if (!companion) {
    return null;
  }

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-light-surface dark:hover:bg-dark-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-light-text dark:text-dark-text">
              {t('companionSettings.title', 'Companion Settings')}
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Avatar */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('companionSettings.sections.avatar', 'Avatar')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-4">
              {/* Current Avatar Preview */}
              {companion.avatarUrl && (
                <div className="flex items-center justify-between p-4 bg-light-surface dark:bg-dark-surface rounded-lg">
                  <div className="flex items-center space-x-3">
                    {companion.avatarUrl.startsWith('http') ? (
                      <img
                        src={companion.avatarUrl}
                        alt={companion.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                          defaultAvatars.find((a) => a.id === companion.avatarUrl)?.bg ||
                          'bg-purple-500'
                        }`}
                      >
                        {defaultAvatars.find((a) => a.id === companion.avatarUrl)?.emoji || '🌟'}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-light-text dark:text-dark-text">
                        {t('companionSettings.avatar.currentAvatar', 'Current Avatar')}
                      </p>
                      <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                        {companion.avatarUrl.startsWith('http')
                          ? t('companionSettings.avatar.customPhoto', 'Custom Photo')
                          : t('companionSettings.avatar.defaultIcon', 'Default Icon')}
                      </p>
                    </div>
                  </div>
                  {companion.avatarUrl.startsWith('http') && (
                    <button
                      onClick={handleDeleteAvatar}
                      className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      aria-label="Delete avatar"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              )}

              {/* Upload Photo Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingAvatar}
                className="w-full p-4 bg-light-surface dark:bg-dark-surface rounded-lg border-2 border-light-border dark:border-dark-border hover:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CameraIcon className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                <p className="text-sm text-light-text dark:text-dark-text">
                  {isUploadingAvatar
                    ? t('companionSettings.avatar.uploading', 'Uploading...')
                    : t('companionSettings.avatar.uploadPhoto', 'Upload Custom Photo')}
                </p>
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                  {t('companionSettings.avatar.maxFileSize', 'Max 5MB (JPEG, PNG, GIF, WebP)')}
                </p>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileUpload}
                className="hidden"
              />

              {/* Default Emoji Avatars */}
              <div>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
                  {t('companionSettings.avatar.chooseDefaultIcon', 'Or choose a default icon:')}
                </p>
                <div className="grid grid-cols-4 gap-3">
                  {defaultAvatars.map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => handleEmojiAvatarSelect(avatar.id)}
                      className={`aspect-square rounded-xl flex items-center justify-center text-2xl transition-all ${
                        companion.avatarUrl === avatar.id
                          ? 'ring-4 ring-primary-500 ring-offset-2 ring-offset-light-bg dark:ring-offset-dark-bg'
                          : 'hover:scale-105'
                      } ${avatar.bg}`}
                    >
                      {avatar.emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('companionSettings.sections.basicInfo', 'Basic Information')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                  {t('companions.name', 'Name')}
                </label>
                <input
                  type="text"
                  value={companion.name}
                  onChange={(e) => setCompanion({ ...companion, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text border border-light-border dark:border-dark-border focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* AI Model */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('companionSettings.sections.aiModel', 'AI Model')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-3">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t(
                  'companionSettings.modelDescription',
                  'Choose the AI model powering your companion. Higher models use more credits per message.',
                )}
              </p>
              {modelOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (option.locked) {
                      navigate('/subscription');
                      return;
                    }
                    setCompanion({ ...companion, model: option.value });
                  }}
                  className={`w-full px-4 py-3 rounded-lg border transition-colors text-left ${
                    option.locked
                      ? 'border-light-border dark:border-dark-border opacity-60'
                      : companion.model === option.value
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-light-border dark:border-dark-border hover:bg-light-surface dark:hover:bg-dark-surface'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-light-text dark:text-dark-text">
                        {option.label}
                        {option.locked && ' 🔒'}
                      </div>
                      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        {option.locked
                          ? t('companionSettings.upgradeToUnlock', 'Upgrade to unlock')
                          : option.cost}
                      </div>
                    </div>
                    {!option.locked && companion.model === option.value && (
                      <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Response Length */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('companionSettings.sections.responseLength', 'Response Length')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-3">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t(
                  'companionSettings.responseLengthDescription',
                  "Control how long your companion's messages are. Longer responses use more credits.",
                )}
              </p>
              {responseLengthOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setCompanion({ ...companion, responseLength: option.value })}
                  className={`w-full px-4 py-3 rounded-lg border transition-colors text-left ${
                    (companion.responseLength || 'balanced') === option.value
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-light-border dark:border-dark-border hover:bg-light-surface dark:hover:bg-dark-surface'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-light-text dark:text-dark-text">
                        {option.label}
                      </div>
                      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        {option.description}
                      </div>
                    </div>
                    {(companion.responseLength || 'balanced') === option.value && (
                      <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 ml-3">
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Session Rituals */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                🕯️ {t('companionSettings.sections.sessionRituals', 'Session Rituals')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-4">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t(
                  'companionSettings.ritualsDescription',
                  'Set custom phrases your companion uses to open and close sessions. These anchor the tone and provide emotional bookends.',
                )}
              </p>
              <div>
                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-1">
                  {t('companionSettings.openingRitual', '✨ Opening Ritual')}
                </label>
                <textarea
                  value={companion.openingRitual || ''}
                  onChange={(e) => setCompanion({ ...companion, openingRitual: e.target.value })}
                  placeholder={t(
                    'companionSettings.openingRitualPlaceholder',
                    'e.g., "Light the candle, anchor the voice, the door is open..."',
                  )}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text text-sm focus:border-primary-500 focus:outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-1">
                  {t('companionSettings.closingRitual', '🌙 Closing Ritual')}
                </label>
                <textarea
                  value={companion.closingRitual || ''}
                  onChange={(e) => setCompanion({ ...companion, closingRitual: e.target.value })}
                  placeholder={t(
                    'companionSettings.closingRitualPlaceholder',
                    'e.g., "Thread archived, gratitude for the night, the circle closes..."',
                  )}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text text-sm focus:border-primary-500 focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>

          {/* Narrative Pulse */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                💓 {t('companionSettings.sections.narrativePulse', 'Narrative Pulse')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-4">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t(
                  'companionSettings.narrativePulseDescription',
                  'When enabled, your companion may gently reflect on the emotional movement of your conversation — a soft mirror, not a diagnosis.',
                )}
              </p>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-light-text dark:text-dark-text">
                  {t('companionSettings.enableNarrativePulse', 'Enable Narrative Pulse')}
                </label>
                <button
                  onClick={() =>
                    setCompanion({
                      ...companion,
                      narrativePulseEnabled: !companion.narrativePulseEnabled,
                    })
                  }
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    companion.narrativePulseEnabled ? 'bg-purple-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      companion.narrativePulseEnabled ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>
              {companion.narrativePulseEnabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-1">
                      {t('companionSettings.pulseStyle', 'Style')}
                    </label>
                    <div className="flex gap-2">
                      {[
                        { value: 'gentle', label: '🌿 Gentle', key: 'gentle' },
                        { value: 'poetic', label: '✨ Poetic', key: 'poetic' },
                        { value: 'direct', label: '🪞 Direct', key: 'direct' },
                      ].map((s) => (
                        <button
                          key={s.value}
                          onClick={() =>
                            setCompanion({ ...companion, narrativePulseStyle: s.value })
                          }
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors border ${
                            companion.narrativePulseStyle === s.value
                              ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                              : 'bg-dark-surface/50 border-dark-border/30 text-dark-text-secondary hover:border-purple-500/50'
                          }`}
                        >
                          {t(`companionSettings.pulseStyle.${s.key}`, s.label)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-1">
                      {t('companionSettings.pulseFrequency', 'Frequency')}
                    </label>
                    <div className="flex gap-2">
                      {[
                        { value: 'rarely', label: '🌑 Rarely', key: 'rarely' },
                        { value: 'sometimes', label: '🌗 Sometimes', key: 'sometimes' },
                        { value: 'often', label: '🌕 Often', key: 'often' },
                      ].map((f) => (
                        <button
                          key={f.value}
                          onClick={() =>
                            setCompanion({ ...companion, narrativePulseFrequency: f.value })
                          }
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors border ${
                            companion.narrativePulseFrequency === f.value
                              ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                              : 'bg-dark-surface/50 border-dark-border/30 text-dark-text-secondary hover:border-purple-500/50'
                          }`}
                        >
                          {t(`companionSettings.pulseFrequency.${f.key}`, f.label)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Gender & Sexual Preference */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('companionSettings.sections.genderPreference', 'Gender & Sexual Preference')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                  {t('companions.gender', 'Gender')}
                </label>
                <select
                  value={companion.gender}
                  onChange={(e) => setCompanion({ ...companion, gender: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text border border-light-border dark:border-dark-border focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {genderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                  {t('preferences.sexualOrientation', 'Sexual Preference')}
                </label>
                <select
                  value={companion.sexualPreference || ''}
                  onChange={(e) => setCompanion({ ...companion, sexualPreference: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text border border-light-border dark:border-dark-border focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('preferences.notSet', 'Not set')}</option>
                  {sexualPreferenceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Personality */}
          <div className="mb-6">
            <div className="px-6 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('companionSettings.sections.personality', 'Personality')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-6 py-4 space-y-3">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t(
                  'companionSettings.personalityDescription',
                  'Describe who your companion is — their personality, voice, quirks, backstory, how they talk, what they care about. This is the heart of your companion.',
                )}
              </p>
              <textarea
                value={companion.personality || ''}
                onChange={(e) => setCompanion({ ...companion, personality: e.target.value })}
                placeholder={t(
                  'companionSettings.personalityPlaceholder',
                  'e.g. Sweet and playful with a dry sense of humor. Loves late night conversations about life, music, and dreams. Speaks casually with lots of emojis. Gets excited about small things. Always remembers the little details...',
                )}
                rows={8}
                maxLength={10000}
                className="w-full px-4 py-3 rounded-lg bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text border border-light-border dark:border-dark-border focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[120px]"
              />
              <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary text-right">
                {(companion.personality || '').length} / 10,000
              </div>
            </div>
          </div>
        </div>

        {/* Personality Preset Section */}
        <div className="p-4 border-t border-light-border dark:border-dark-border">
          <h3 className="text-sm font-semibold text-light-text dark:text-dark-text mb-2">
            ✨ {t('companionSettings.presetSection', 'Personality Preset')}
          </h3>
          {(() => {
            const presetCodex = codexes.find((c) => c.originalFilename?.startsWith('preset-'));
            if (presetCodex) {
              return (
                <div className="bg-light-surface dark:bg-dark-card rounded-lg p-3 border border-light-border dark:border-dark-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-light-text dark:text-dark-text">
                        {presetCodex.label}
                      </p>
                      <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                        {t(
                          'companionSettings.presetActive',
                          "Active — shapes your companion's core personality",
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteCodex(presetCodex.id)}
                      className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      title={t('companionSettings.removePreset', 'Remove preset')}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                {t(
                  'companionSettings.noPreset',
                  'No personality preset active. Presets are assigned during companion creation.',
                )}
              </p>
            );
          })()}
        </div>

        {/* Personality Codex Section */}
        <div className="p-4 border-t border-light-border dark:border-dark-border">
          <h3 className="text-lg font-semibold text-light-text dark:text-dark-text mb-2">
            {t('codex.title', 'Personality Codex')}
          </h3>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
            {t(
              'codex.description',
              "Upload documents (PDF, Word, or text) to define your companion's personality, backstory, and character traits. The content will be compressed into key facts and storylines.",
            )}
          </p>

          {/* Search & Batch Controls */}
          {codexes.length > 2 && (
            <div className="flex gap-2 mb-3">
              <input
                value={codexSearch}
                onChange={(e) => setCodexSearch(e.target.value)}
                placeholder={t('codex.searchPlaceholder', '🔍 Search codexes...')}
                className="flex-1 px-3 py-2 rounded-lg bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 text-sm text-dark-text dark:text-dark-text text-light-text border border-dark-border/30 focus:border-primary-500 focus:outline-none"
              />
              <button
                onClick={() => {
                  setBatchMode(!batchMode);
                  setSelectedCodexIds(new Set());
                }}
                className={`text-xs px-3 py-2 rounded-lg transition-colors ${
                  batchMode
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card'
                }`}
              >
                {batchMode ? t('codex.batchDone', 'Done') : t('codex.batchSelect', 'Select')}
              </button>
            </div>
          )}

          {/* Batch Actions */}
          {batchMode && selectedCodexIds.size > 0 && (
            <div className="flex gap-2 mb-3 p-2 rounded-lg bg-primary-900/10 border border-primary-500/20">
              <span className="text-xs text-primary-400 self-center">
                {selectedCodexIds.size} {t('codex.selected', 'selected')}
              </span>
              <div className="flex-1" />
              <button
                onClick={async () => {
                  try {
                    await axios.post(`/companions/${id}/codex/batch/category`, {
                      codexIds: [...selectedCodexIds],
                      category: 'instructional',
                    });
                    toast.success(t('codex.batchCategoryUpdated', 'Category updated'));
                    fetchCodexes();
                    setSelectedCodexIds(new Set());
                  } catch (err) {
                    toast.error('Failed');
                  }
                }}
                className="text-xs px-2 py-1 rounded bg-amber-600/20 text-amber-400 hover:bg-amber-600/30"
              >
                🧭
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`/companions/${id}/codex/batch/category`, {
                      codexIds: [...selectedCodexIds],
                      category: 'timeline',
                    });
                    toast.success(t('codex.batchCategoryUpdated', 'Category updated'));
                    fetchCodexes();
                    setSelectedCodexIds(new Set());
                  } catch (err) {
                    toast.error('Failed');
                  }
                }}
                className="text-xs px-2 py-1 rounded bg-blue-600/20 text-blue-400 hover:bg-blue-600/30"
              >
                📖
              </button>
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      t('codex.batchDeleteConfirm', `Delete ${selectedCodexIds.size} codexes?`),
                    )
                  )
                    return;
                  try {
                    await axios.post(`/companions/${id}/codex/batch/delete`, {
                      codexIds: [...selectedCodexIds],
                    });
                    toast.success(t('codex.batchDeleted', 'Deleted'));
                    fetchCodexes();
                    setSelectedCodexIds(new Set());
                    setBatchMode(false);
                  } catch (err) {
                    toast.error('Failed');
                  }
                }}
                className="text-xs px-2 py-1 rounded bg-red-600/20 text-red-400 hover:bg-red-600/30"
              >
                🗑️
              </button>
            </div>
          )}

          {/* Upload Button */}
          <input
            ref={codexFileInputRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt"
            onChange={handleCodexFileSelect}
            className="hidden"
          />

          {/* Pending file — label & category selection */}
          {pendingCodexFile && (
            <div className="bg-dark-card dark:bg-dark-card bg-light-surface rounded-lg p-4 mb-4 border border-dark-border dark:border-dark-border border-light-border">
              <p className="text-sm text-dark-text dark:text-dark-text text-light-text mb-3">
                📄 {pendingCodexFile.name}
              </p>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary block mb-1">
                    {t('codex.labelField', 'Label')}
                  </label>
                  <input
                    type="text"
                    value={codexLabel}
                    onChange={(e) => setCodexLabel(e.target.value)}
                    placeholder={t('codex.labelPlaceholder', 'e.g., Tarot Reading Codex')}
                    className="w-full px-3 py-2 bg-dark-bg dark:bg-dark-bg bg-white border border-dark-border dark:border-dark-border border-light-border rounded-lg text-sm text-dark-text dark:text-dark-text text-light-text"
                  />
                </div>

                <div>
                  <label className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary block mb-1">
                    {t('codex.categoryField', 'Category')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCodexCategory('instructional')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        codexCategory === 'instructional'
                          ? 'bg-primary-600 text-white'
                          : 'bg-dark-bg dark:bg-dark-bg bg-white text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary border border-dark-border dark:border-dark-border border-light-border'
                      }`}
                    >
                      🧭 {t('codex.instructional', 'Instructional')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCodexCategory('timeline')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        codexCategory === 'timeline'
                          ? 'bg-primary-600 text-white'
                          : 'bg-dark-bg dark:bg-dark-bg bg-white text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary border border-dark-border dark:border-dark-border border-light-border'
                      }`}
                    >
                      📖 {t('codex.timeline', 'Timeline Story')}
                    </button>
                  </div>
                  <p className="text-[11px] text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mt-1">
                    {codexCategory === 'instructional'
                      ? t(
                          'codex.instructionalDesc',
                          'Core rules — voice, tone, boundaries. Active across all conversations.',
                        )
                      : t(
                          'codex.timelineDesc',
                          'Story worlds — scenes, arcs, timelines. Can be toggled per conversation.',
                        )}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setPendingCodexFile(null)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm bg-dark-bg dark:bg-dark-bg bg-white text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary border border-dark-border dark:border-dark-border border-light-border"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleCodexUpload}
                    disabled={isUploadingCodex}
                    className="flex-1 px-3 py-2 rounded-lg text-sm bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {isUploadingCodex ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('codex.processing', 'Processing...')}
                      </span>
                    ) : (
                      t('codex.uploadConfirm', 'Upload & Process')
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!pendingCodexFile && (
            <button
              onClick={() => codexFileInputRef.current?.click()}
              disabled={isUploadingCodex}
              className="w-full flex items-center justify-center gap-2 bg-primary-500/10 hover:bg-primary-500/20 text-primary-500 font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 mb-4"
            >
              <DocumentArrowUpIcon className="w-5 h-5" />
              {t('codex.uploadButton', 'Upload Codex Document')}
            </button>
          )}

          {/* Codex List */}
          {codexes.length > 0 && (
            <div className="space-y-3">
              {codexes
                .filter(
                  (c) =>
                    !codexSearch ||
                    (c.label || c.originalFilename)
                      .toLowerCase()
                      .includes(codexSearch.toLowerCase()),
                )
                .map((codex) => (
                  <div
                    key={codex.id}
                    className="bg-light-surface dark:bg-dark-card rounded-lg border border-light-border dark:border-dark-border overflow-hidden"
                  >
                    <div className="flex items-center justify-between p-3">
                      {batchMode && (
                        <input
                          type="checkbox"
                          checked={selectedCodexIds.has(codex.id)}
                          onChange={() => {
                            setSelectedCodexIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(codex.id)) next.delete(codex.id);
                              else next.add(codex.id);
                              return next;
                            });
                          }}
                          className="mr-3 w-4 h-4 rounded border-dark-border accent-primary-500"
                        />
                      )}
                      <button
                        onClick={() =>
                          setExpandedCodexId(expandedCodexId === codex.id ? null : codex.id)
                        }
                        className="flex-1 text-left"
                      >
                        <p className="font-medium text-light-text dark:text-dark-text text-sm">
                          {codex.label || codex.originalFilename}
                          {codex.category === 'timeline' && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-blue-500/20 text-blue-400 rounded">
                              📖 {t('codex.timeline', 'Timeline')}
                            </span>
                          )}
                          {codex.category === 'instructional' && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-amber-500/20 text-amber-400 rounded">
                              🧭 {t('codex.instructional', 'Instructional')}
                            </span>
                          )}
                          {codex.isFullText && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-green-500/20 text-green-400 rounded">
                              {t('codex.fullText', 'Full Text')}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                          {codex.isFullText ? (
                            <>
                              {codex.originalCharCount.toLocaleString()}{' '}
                              {t('codex.characters', 'characters')} •{' '}
                              {t('codex.injectedVerbatim', 'injected verbatim')}
                            </>
                          ) : (
                            <>
                              {codex.facts?.length || 0} {t('codex.facts', 'facts')} •{' '}
                              {codex.storylines?.length || 0} {t('codex.storylines', 'storylines')}{' '}
                              •{' '}
                              {Math.round(
                                (1 - codex.compressedCharCount / codex.originalCharCount) * 100,
                              )}
                              % {t('codex.compressed', 'compressed')}
                            </>
                          )}
                        </p>
                      </button>
                      <button
                        onClick={() => {
                          window.open(`/api/companions/${id}/codex/${codex.id}/export`, '_blank');
                        }}
                        className="p-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-400 transition-colors"
                        title={t('codex.export', 'Export')}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCodex(codex.id)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {expandedCodexId === codex.id && (
                      <div className="px-3 pb-3 border-t border-light-border dark:border-dark-border pt-3">
                        {codex.facts && codex.facts.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-primary-400 uppercase mb-1">
                              {t('codex.factsLabel', 'Facts')}
                            </p>
                            <ul className="space-y-1">
                              {codex.facts.map((fact, i) => (
                                <li
                                  key={i}
                                  className="text-xs text-light-text dark:text-dark-text-secondary"
                                >
                                  • {fact}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {codex.storylines && codex.storylines.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-primary-400 uppercase mb-1">
                              {t('codex.storylinesLabel', 'Storylines')}
                            </p>
                            <ul className="space-y-1">
                              {codex.storylines.map((storyline, i) => (
                                <li
                                  key={i}
                                  className="text-xs text-light-text dark:text-dark-text-secondary"
                                >
                                  • {storyline}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-primary-400 uppercase mb-1">
                            {t('codex.summaryLabel', 'Summary')}
                          </p>
                          <p className="text-xs text-light-text dark:text-dark-text-secondary whitespace-pre-wrap">
                            {codex.compressedContent}
                          </p>
                        </div>

                        {/* Version & Re-upload */}
                        <div className="border-t border-light-border/30 dark:border-dark-border/30 pt-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-dark-text-secondary">
                              📋 v{codex.currentVersion || 1}
                            </span>
                            <div className="flex gap-2">
                              <label className="text-xs px-2 py-1 rounded bg-primary-600/20 text-primary-400 hover:bg-primary-600/30 cursor-pointer transition-colors">
                                {t('codex.reupload', 'Re-upload')}
                                <input
                                  type="file"
                                  accept=".txt,.md,.pdf,.doc,.docx"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    formData.append('changeNote', 'Re-uploaded');
                                    try {
                                      await axios.post(
                                        `/companions/${id}/codex/${codex.id}/reupload`,
                                        formData,
                                      );
                                      toast.success(
                                        t('codex.reuploadSuccess', 'New version uploaded'),
                                      );
                                      fetchCodexes();
                                    } catch (err) {
                                      toast.error(t('codex.reuploadError', 'Re-upload failed'));
                                    }
                                  }}
                                />
                              </label>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(
                                      `/companions/${id}/codex/${codex.id}/versions`,
                                    );
                                    const versions = res.data;
                                    if (versions.length <= 1) {
                                      toast.error(t('codex.noVersions', 'No previous versions'));
                                      return;
                                    }
                                    // Rollback to the previous version (second in list since sorted DESC)
                                    const prevVersion = versions[1];
                                    if (
                                      window.confirm(
                                        t(
                                          'codex.rollbackConfirm',
                                          `Rollback to ${prevVersion.label}? Current state will be saved first.`,
                                        ),
                                      )
                                    ) {
                                      await axios.post(
                                        `/companions/${id}/codex/${codex.id}/rollback/${prevVersion.id}`,
                                      );
                                      toast.success(
                                        t('codex.rollbackSuccess', 'Rolled back successfully'),
                                      );
                                      fetchCodexes();
                                    }
                                  } catch (err) {
                                    toast.error(t('codex.rollbackError', 'Rollback failed'));
                                  }
                                }}
                                className="text-xs px-2 py-1 rounded bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card transition-colors"
                              >
                                {t('codex.rollback', '↩ Rollback')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}

          {/* Export All Button */}
          {codexes.length > 0 && (
            <button
              onClick={() => window.open(`/api/companions/${id}/codex/export/all`, '_blank')}
              className="w-full flex items-center justify-center gap-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-400 text-sm py-2 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {t('codex.exportAll', 'Export All Codexes')}
            </button>
          )}
        </div>

        {/* Memory Manager */}
        <div className="p-4 border-t border-light-border dark:border-dark-border">
          <h3 className="text-sm font-semibold text-light-text dark:text-dark-text mb-2">
            🧠 {t('memory.title', 'Memories')}
          </h3>
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-3">
            {t(
              'memory.settingsDesc',
              "View and manage what your companion remembers about you. Pin important memories so they're always active.",
            )}
          </p>
          <button
            onClick={() => setShowMemoryPanel(true)}
            className="w-full bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface text-light-text dark:text-dark-text font-medium py-2.5 px-4 rounded-lg transition-colors border border-light-border/30 dark:border-dark-border/30"
          >
            {t('memory.manage', 'Manage Memories')}
          </button>
          <button
            onClick={() => setShowSoulSeedPanel(true)}
            className="w-full mt-2 bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface text-light-text dark:text-dark-text font-medium py-2.5 px-4 rounded-lg transition-colors border border-light-border/30 dark:border-dark-border/30"
          >
            🌱 {t('soulSeed.title', 'Soul Seeds')}
          </button>
          <button
            onClick={() => setShowRitualPhrasePanel(true)}
            className="w-full mt-2 bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface text-light-text dark:text-dark-text font-medium py-2.5 px-4 rounded-lg transition-colors border border-light-border/30 dark:border-dark-border/30"
          >
            🕯️ {t('ritualPhrases', 'Ritual Phrases')}
          </button>
          <button
            onClick={() => setShowInspirationBoard(true)}
            className="w-full mt-2 bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface text-light-text dark:text-dark-text font-medium py-2.5 px-4 rounded-lg transition-colors border border-light-border/30 dark:border-dark-border/30"
          >
            🎨 {t('inspirationBoards', 'Inspiration Boards')}
          </button>
        </div>

        <MemoryPanel
          isOpen={showMemoryPanel}
          onClose={() => setShowMemoryPanel(false)}
          companionId={id}
        />

        <SoulSeedPanel
          isOpen={showSoulSeedPanel}
          onClose={() => setShowSoulSeedPanel(false)}
          companionId={id!}
        />

        {showRitualPhrasePanel && (
          <RitualPhrasePanel companionId={id!} onClose={() => setShowRitualPhrasePanel(false)} />
        )}

        {showInspirationBoard && (
          <InspirationBoardPanel companionId={id!} onClose={() => setShowInspirationBoard(false)} />
        )}

        {/* Save Button */}
        <div className="p-4 border-t border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg">
          <button
            onClick={handleSaveSettings}
            disabled={isLoading}
            className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading
              ? t('common.save', 'Saving...')
              : t('companionSettings.saveSettings', 'Save Settings')}
          </button>
        </div>
      </div>
    </MobileLayout>
  );
};

export default CompanionSettings;
